/**
 * Lot 335 · Bloc 1 — Preview d'audience live (fidèle).
 *
 * Quand le SiteEditor ouvre une page dans son iframe, il injecte un query
 * param `_preview=public|investor|sponsor`. Ce hook le lit, s'assure que
 * l'utilisateur courant est admin (sinon aucun effet), puis expose :
 *
 *   - audience : 'public' | 'investor' | 'sponsor' | null (null = rendu natif)
 *   - isPreviewing : boolean
 *   - effectiveAuth : boolean — ce que la page doit considérer comme "l'utilisateur connecté"
 *                              pour simuler fidèlement l'audience cible
 *
 * Le hook est volontairement léger : c'est à chaque page publique de lire
 * `isPreviewing` + `audience` pour brancher les bons endpoints et masquer
 * les sections réservées à une autre audience.
 *
 * Règle de sécurité : un non-admin ne peut jamais activer le mode preview
 * (on ignore silencieusement le query param).
 */
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ADMIN_ROLES = new Set(['super_admin', 'admin']);

export function usePreviewAudience() {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();

  return useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('_preview');
    if (!raw) return { audience: null, isPreviewing: false, effectiveAuth: isAuthenticated };
    if (!user || !ADMIN_ROLES.has(user.role)) {
      return { audience: null, isPreviewing: false, effectiveAuth: isAuthenticated };
    }
    const clean = raw.toLowerCase();
    if (!['public', 'investor', 'sponsor'].includes(clean)) {
      return { audience: null, isPreviewing: false, effectiveAuth: isAuthenticated };
    }
    // Simuler l'auth de l'audience
    let effectiveAuth;
    if (clean === 'public') effectiveAuth = false;          // visiteur anonyme
    else if (clean === 'investor') effectiveAuth = true;    // investisseur connecté
    else if (clean === 'sponsor') effectiveAuth = true;     // sponsor connecté
    else effectiveAuth = isAuthenticated;
    return { audience: clean, isPreviewing: true, effectiveAuth };
  }, [location.search, user, isAuthenticated]);
}
